/**
 * canvas-engine/renderer/editGeometry.js
 *
 * OWN, self-contained PURE geometry helpers for the Canvas Engine's BUILDER-mode
 * on-canvas editing (drag / resize / snap / clamp). These power the in-frame
 * selection box rendered by `CanvasRenderer` in builder mode so the engine stays
 * self-contained — it does NOT import from `admin-console-ui/src/modules/
 * canvas-builder/` (or template-builder). The functions are ported, 1:1, from
 * the canvas-builder's own `preview/layoutCore.js` so the geometry contract is
 * identical.
 *
 * Coordinate model (matches the Canvas_Model frame, absolute positioning):
 *   - A `rect` is `{ x, y, width, height }` in pixels at the Reference_Width.
 *   - `x` / `y` are the element's top-left offset inside the `position:relative`
 *     section frame (the same origin the element wrapper uses), so a selection
 *     box rendered inside that frame aligns 1:1 with the element.
 *   - A `frame` is `{ width, height }` — the canvas frame's content box. When a
 *     frame dimension is omitted/non-finite it is treated as unbounded
 *     (Infinity) so geometry still composes safely.
 *
 * The shapes returned line up with the `patch.frame = { x, y, width, height }`
 * REPLACE contract consumed by `applyCanvasPatch`.
 *
 * Pure functions only: every export takes plain numbers/objects and returns NEW
 * plain objects, never mutating its inputs. No React, no DOM.
 */

// ── Internal numeric helpers ────────────────────────────────────────────────

/** Finite number or fallback. */
function num(value, fallback) {
  return Number.isFinite(value) ? value : fallback;
}

/** Clamp `value` into the inclusive [min, max] range (tolerates min > max). */
function clamp(value, min, max) {
  let lo = min;
  let hi = max;
  if (lo > hi) {
    const t = lo;
    lo = hi;
    hi = t;
  }
  if (value < lo) return lo;
  if (value > hi) return hi;
  return value;
}

/** Normalize a `frame` into `{ width, height }` with unbounded defaults. */
function normalizeFrame(frame) {
  const f = frame && typeof frame === 'object' ? frame : {};
  return {
    width: Number.isFinite(f.width) ? f.width : Infinity,
    height: Number.isFinite(f.height) ? f.height : Infinity,
  };
}

/** Normalize a `minSize` into `{ width, height }` (number or object). */
function normalizeMinSize(minSize) {
  if (typeof minSize === 'number' && minSize > 0) {
    return { width: minSize, height: minSize };
  }
  if (minSize && typeof minSize === 'object') {
    return {
      width: Number.isFinite(minSize.width) && minSize.width > 0 ? minSize.width : DEFAULT_MIN_SIZE,
      height: Number.isFinite(minSize.height) && minSize.height > 0 ? minSize.height : DEFAULT_MIN_SIZE,
    };
  }
  return { width: DEFAULT_MIN_SIZE, height: DEFAULT_MIN_SIZE };
}

/** Coerce any rect-ish input into a finite `{ x, y, width, height }`. */
function normalizeRect(rect) {
  const r = rect && typeof rect === 'object' ? rect : {};
  return {
    x: num(r.x, 0),
    y: num(r.y, 0),
    width: Math.max(num(r.width, 0), 0),
    height: Math.max(num(r.height, 0), 0),
  };
}

// ── Public constants ─────────────────────────────────────────────────────────

/** Smallest width/height an element may be resized to (px). */
export const DEFAULT_MIN_SIZE = 8;

/** The eight resize handles the selection box exposes. */
export const RESIZE_HANDLES = Object.freeze(['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw']);

// ── Snapping ──────────────────────────────────────────────────────────────────

/**
 * Snap a single scalar to the nearest multiple of `grid`. A falsy/non-positive
 * grid is a no-op (returns the value unchanged), so snapping is opt-in.
 *
 * @param {number} value
 * @param {number} [grid] grid step in px; 0/undefined → no snap
 * @returns {number}
 */
export function snap(value, grid) {
  const v = num(value, 0);
  if (!Number.isFinite(grid) || grid <= 0) return v;
  return Math.round(v / grid) * grid;
}

// ── Constraining ──────────────────────────────────────────────────────────────

/**
 * Keep an element rect fully within the frame bounds. Size is preserved; only
 * position is adjusted. If larger than the frame on an axis, it is pinned to
 * that axis's origin (0). Lower bound is always 0 (the frame origin).
 *
 * @param {{x:number,y:number,width:number,height:number}} rect
 * @param {{width:number,height:number}} [frame]
 * @returns {{x:number,y:number,width:number,height:number}} a NEW clamped rect
 */
export function clampToFrame(rect, frame) {
  const r = normalizeRect(rect);
  const f = normalizeFrame(frame);

  const maxX = Number.isFinite(f.width) ? Math.max(0, f.width - r.width) : Infinity;
  const maxY = Number.isFinite(f.height) ? Math.max(0, f.height - r.height) : Infinity;

  return {
    x: clamp(r.x, 0, maxX),
    y: clamp(r.y, 0, maxY),
    width: r.width,
    height: r.height,
  };
}

// ── Drag ──────────────────────────────────────────────────────────────────────

/**
 * Apply a pointer drag to a starting rect: translate by (dx, dy) and clamp the
 * result inside the frame. Size is unchanged, so only the new `{ x, y }` is
 * returned.
 *
 * @param {{x:number,y:number,width:number,height:number}} startRect rect at drag start
 * @param {number} dx pointer delta x (px)
 * @param {number} dy pointer delta y (px)
 * @param {{width:number,height:number}} [frame]
 * @returns {{x:number,y:number}} the new clamped top-left
 */
export function applyDrag(startRect, dx, dy, frame) {
  const r = normalizeRect(startRect);
  const moved = { x: r.x + num(dx, 0), y: r.y + num(dy, 0), width: r.width, height: r.height };
  const clamped = clampToFrame(moved, frame);
  return { x: clamped.x, y: clamped.y };
}

// ── Resize ──────────────────────────────────────────────────────────────────

/**
 * Apply a pointer drag on a resize handle to a starting rect. The handle id is
 * any combination of `n`/`s` (vertical edge) and `e`/`w` (horizontal edge),
 * i.e. one of `n, s, e, w, ne, nw, se, sw`. Edge movement respects a minimum
 * width/height (`minSize`) and the frame bounds. Returns the full new rect.
 *
 * @param {{x:number,y:number,width:number,height:number}} startRect rect at resize start
 * @param {('n'|'s'|'e'|'w'|'ne'|'nw'|'se'|'sw')} handle the dragged handle
 * @param {number} dx pointer delta x (px)
 * @param {number} dy pointer delta y (px)
 * @param {{width:number,height:number}} [frame]
 * @param {number|{width:number,height:number}} [minSize] min element size
 * @returns {{x:number,y:number,width:number,height:number}} a NEW rect
 */
export function applyResize(startRect, handle, dx, dy, frame, minSize) {
  const r = normalizeRect(startRect);
  const f = normalizeFrame(frame);
  const min = normalizeMinSize(minSize);
  const h = typeof handle === 'string' ? handle : '';
  const ddx = num(dx, 0);
  const ddy = num(dy, 0);

  let left = r.x;
  let top = r.y;
  let right = r.x + r.width;
  let bottom = r.y + r.height;

  // West edge; clamp to [0, right - minWidth].
  if (h.indexOf('w') !== -1) {
    const maxLeft = right - min.width;
    left = clamp(r.x + ddx, 0, maxLeft);
  }
  // East edge; clamp to [left + minWidth, frame.width].
  if (h.indexOf('e') !== -1) {
    const maxRight = Number.isFinite(f.width) ? f.width : Infinity;
    right = clamp(r.x + r.width + ddx, left + min.width, maxRight);
  }
  // North edge; clamp to [0, bottom - minHeight].
  if (h.indexOf('n') !== -1) {
    const maxTop = bottom - min.height;
    top = clamp(r.y + ddy, 0, maxTop);
  }
  // South edge; clamp to [top + minHeight, frame.height].
  if (h.indexOf('s') !== -1) {
    const maxBottom = Number.isFinite(f.height) ? f.height : Infinity;
    bottom = clamp(r.y + r.height + ddy, top + min.height, maxBottom);
  }

  return {
    x: left,
    y: top,
    width: Math.max(right - left, min.width),
    height: Math.max(bottom - top, min.height),
  };
}
